import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useLocation, useNavigate, useParams } from 'react-router'
import { canDock, turnContextFor } from './route-context'
import { usePcmVoice } from './use-pcm-voice'
import type { OrbState } from './voice-orb'
import { AgentSurface } from './agent-surface'
import { useAgentParts } from './use-agent-parts'
import { installDevParts } from './dev-parts'
import { useAttachments } from './use-attachments'
import { usePanels } from './use-panels'
import { compileScene, type DiagramScene } from './diagram/scene'
import { ContextPanel } from './panels/context-panel'
import { PagesPanel } from './panels/pages-panel'
import { WorkspacePanel } from './panels/workspace-panel'
import type { PanelId } from './panels/types'
import type { TranscriptTurn } from './parts/transcript'
import { useAudioLevel } from './use-audio-level'
import { useCan } from '@/access/access.provider'
import config from '@/config'
import { getToken } from '@/utils/AuthTokenHelper'
import useUser from '@/hooks/useUser'
// Names, not ids. The serial and the org uuid are true and useless to a
// person; the alias and the organisation's name are what they recognise.
import { useVenues } from '@/hooks/useVenue'
import useSelectedOrganisation from '@/state/entities/organisations/hooks/useSelectedOrganisation'
import {
  useSupportChat,
  type SupportDiagramCommand,
  type SupportNavigateCommand,
  type SupportPartCommand,
  type SupportVoiceState,
} from '@/pages/[org_id]/support/use-support-chat'

/**
 * The support layer: one pill, mounted above the router outlet.
 *
 * WHY IT LIVES HERE and not on a page. Support used to be a destination, and
 * the answer to most of what people ask is a PLACE in the product, so getting
 * the answer meant leaving the conversation to go and use it. Mounted in the
 * organisation layout, the socket, the case and the heard history sit above
 * the outlet: the agent can change the page underneath itself and the
 * conversation carries on.
 *
 * IT IS THE SAME CASE as the Support page. Same hook, same socket, same
 * `SupportCase` — the pill is a second way in, not a second product. Which is
 * also why it stands down on `/support/*`: two sockets on one case would
 * deliver every message twice, and the page is the better surface when
 * somebody has deliberately opened it.
 *
 * MINIMAL BY DEFAULT, and for now that means the ORB AND NOTHING ELSE.
 * Closed it renders nothing at all; open it is a sphere that moves with the
 * voice. No pill, no composer, no transcript: pressing the chord is
 * somebody deciding to ask out loud, and anything else on screen at that
 * moment is a decision they did not want to make. The typed surface still
 * lives at /support/cases.
 */

/** How long the "Bookings › Tables" chip stays up after a move. */
const MOVED_CHIP_MS = 6_000

export function SupportDock() {
  const { org_id: orgId } = useParams<{ org_id: string }>()
  const location = useLocation()
  const navigate = useNavigate()
  const user = useUser()
  const organisation = useSelectedOrganisation()
  // `findAlias` falls back to the serial, so a venue still missing from
  // the cache reads as its serial rather than as nothing.
  const [, findVenueAlias] = useVenues()

  const [open, setOpen] = useState(false)
  const [expanded, setExpanded] = useState(false)
  const [draft, setDraft] = useState('')
  const [movedTo, setMovedTo] = useState<string | null>(null)
  const [unread, setUnread] = useState(0)

  // The same gate as the Support nav item and the Support routes, so the
  // three cannot drift apart. `nav.support` is open to everyone now, and
  // `useCan` still fails CLOSED outside the access provider, so a dock
  // rendered somewhere unexpected hides rather than reveals.
  const permitted = useCan('nav.support')
  const allowed = permitted && canDock(location.pathname, orgId)

  /**
   * The agent is moving them.
   *
   * Guarded on the path we are ALREADY on: the agent can call the tool twice
   * for the same page in one answer (it re-reads its own last turn), and a
   * second navigate to the current route would scroll the page back to the
   * top under somebody who had started reading it.
   */
  const onNavigate = useCallback(
    (command: SupportNavigateCommand) => {
      setMovedTo(command.label)
      // Compared against the QUERY as well, because the three booking views
      // carry the day in `?date=`: two moves to the same planner on two
      // different days are two different destinations, and comparing paths
      // alone would have treated the second as a repeat and ignored it.
      if (command.path === `${location.pathname}${location.search}`) return
      navigate(command.path)
    },
    [navigate, location.pathname, location.search]
  )

  const turnContext = useMemo(
    () => turnContextFor(location.pathname),
    [location.pathname]
  )

  /**
   * The spoken half.
   *
   * `sendAudio` is read through a ref the hook owns, so the microphone is
   * built once and does not restart every time the socket re-renders.
   */
  const [voiceState, setVoiceState] = useState<SupportVoiceState>('off')
  /**
   * Told to us when the conversation opens: a hosted provider detects turns
   * itself, one we drive cannot, so the browser does. Defaulting to
   * `provider` means an older server simply keeps the old behaviour.
   */
  const [turnDetection, setTurnDetection] = useState<'provider' | 'client'>(
    'provider'
  )
  const chatRef = useRef<{
    sendAudio: (chunk: string) => void
    sendUtterance: (chunk: string) => void
    interruptVoice: () => void
  } | null>(null)
  const audio = usePcmVoice({
    turnDetection,
    onChunk: (chunk) => chatRef.current?.sendAudio(chunk),
    onUtterance: (chunk) => chatRef.current?.sendUtterance(chunk),
    onSpeechStart: () => chatRef.current?.interruptVoice(),
  })

  /**
   * What the agent has put on screen.
   *
   * One part at a time, replaced rather than stacked, cleared the moment
   * it is answered. See `useAgentParts`.
   */
  /**
   * The instruments. Up to three, and they do NOT dim the page: the whole
   * reason they are worth the complexity is that you read them while
   * working. See `use-panels` for the eviction rule.
   */
  const panels = usePanels()
  const parts = useAgentParts()

  /**
   * Whatever is on the canvas. One at a time, replaced by the next.
   *
   * "No, put the deposit step first" is a NEW diagram rather than a
   * second one beside the old.
   */
  const [diagram, setDiagram] = useState<DiagramScene | null>(null)
  const [savingDiagram, setSavingDiagram] = useState<'saving' | 'saved' | null>(
    null,
  )

  /**
   * COMPILED HERE, so the canvas only ever handles elements.
   *
   * Three things open it and only one is the agent: a diagram it drew, a
   * blank one somebody started, and a `.excalidraw` opened out of the
   * workspace. Keeping the compile where there is something to compile
   * leaves the canvas one job.
   */
  const onDiagram = useCallback((command: SupportDiagramCommand) => {
    setSavingDiagram(null)
    setDiagram(
      compileScene({
        title: command.title,
        direction: command.direction,
        nodes: command.nodes.map((node) => ({
          id: node.id,
          label: node.label,
          kind: node.kind as 'step' | 'decision' | 'terminal' | 'note' | undefined,
          group: node.group,
        })),
        edges: command.edges,
      }),
    )
  }, [])

  /**
   * Save it into the organisation's files.
   *
   * The workspace rather than a download: a diagram of how a venue's
   * deposits work belongs with that venue's other files, where the agent
   * can read it back and anybody else on the account can find it. It goes
   * to `diagrams/`, which the workspace panel then lists like anything
   * else, and opens again from there.
   */
  const saveDiagram = useCallback(
    async (filename: string, contents: string) => {
      if (!orgId) return
      setSavingDiagram('saving')
      const token = getToken()?.accessToken ?? ''
      const base = `${config.url.trunk}/organisations/${orgId}/workspace`
      try {
        const presigned = await fetch(`${base}/presign`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            path: `/diagrams/${filename}`,
            mimeType: 'application/json',
            size: contents.length,
          }),
        })
        if (!presigned.ok) throw new Error(String(presigned.status))
        const { uploadUrl } = (await presigned.json()) as { uploadUrl: string }
        const put = await fetch(uploadUrl, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: contents,
        })
        if (!put.ok) throw new Error(String(put.status))
        setSavingDiagram('saved')
      } catch {
        setSavingDiagram(null)
      }
    },
    [orgId],
  )
  const onPart = useCallback(
    (command: SupportPartCommand) => {
      parts.show(command.part, command.mode, command.height)
    },
    [parts],
  )

  // `__agentPart()` in the console, development only. The geometry is the
  // risk on this surface and waiting for a model to choose a tool is a
  // terrible way to look at it. See dev-parts.ts.
  useEffect(() => installDevParts(onPart), [onPart])

  const chat = useSupportChat({
    orgId: orgId ?? '',
    myUid: user?.uid,
    enabled: open && allowed,
    /**
     * EVERY OPENING IS A NEW CONVERSATION.
     *
     * The orb is a keypress to ask one thing, not a thread somebody comes
     * back to, and resuming the last open case meant it answered out of
     * stale context: greeting somebody with the page they were on an hour
     * before, re-reading a lookup nobody had asked for again.
     *
     * Only the first connect of an opening asks for it. Once trunk has
     * answered with a case the socket reconnects by id, so a dropped
     * connection mid-sentence rejoins. Picking a previous conversation back
     * up is a later thing, and it will work by passing its `caseId`.
     */
    fresh: true,
    turnContext,
    onNavigate,
    onPart,
    onDiagram,
    voice: {
      onAudio: audio.play,
      onInterrupt: audio.flush,
      onState: setVoiceState,
      onTurnDetection: setTurnDetection,
    },
  })
  chatRef.current = chat

  // The chip is a receipt, not a status: it says where they were just taken
  // and then gets out of the way.
  useEffect(() => {
    if (movedTo === null) return
    const timer = setTimeout(() => {
      setMovedTo(null)
    }, MOVED_CHIP_MS)
    return () => {
      clearTimeout(timer)
    }
  }, [movedTo])

  // Replies that landed while the transcript was shut. Counted off the
  // assistant's completed turns rather than off every token, so a long answer
  // streaming in is one unread and not two hundred.
  const seenRef = useRef(0)
  const agentTurns = chat.messages.filter(
    (m) => m.role === 'assistant' && m.status !== 'streaming'
  ).length
  useEffect(() => {
    if (expanded) {
      seenRef.current = agentTurns
      setUnread(0)
      return
    }
    setUnread(Math.max(0, agentTurns - seenRef.current))
  }, [agentTurns, expanded])

  // Leaving the organisation, or arriving on the full Support page, stands
  // the dock down. Reopening is one click and keeps the same case.
  useEffect(() => {
    if (!allowed) setExpanded(false)
  }, [allowed])

  /**
   * A spoken conversation outlives several pages, including the ones the
   * agent moved them to, so trunk is told where they are every time the
   * route changes. Typed turns carry their own context, so this is only
   * needed while the microphone is open.
   */
  const { syncContext } = chat
  useEffect(() => {
    if (voiceState === 'listening') syncContext()
  }, [location.pathname, voiceState, syncContext])

  /**
   * WANTING VOICE IS STATE, not an action, and that is the fix for opening
   * and talking in one gesture.
   *
   * The shortcut opens the dock and asks for the microphone at the same
   * moment, but the socket does not exist yet: `enabled` only becomes true
   * once `open` is, and the hook connects after that. Sending `voice_start`
   * straight away posted into a socket in CONNECTING, where the hook drops
   * it silently. The microphone opened, trunk never heard the request, and
   * the orb never appeared because no `voice_status` ever came back.
   *
   * So the press records the intent and the effect below acts on it the
   * moment there is a socket to act through.
   */
  const [wantVoice, setWantVoice] = useState(false)

  useEffect(() => {
    if (!wantVoice) return
    if (chat.connection !== 'open') return
    if (voiceState !== 'off' && voiceState !== 'error') return
    chat.startVoice()
    void audio.start()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wantVoice, chat.connection, voiceState])

  const stopVoice = useCallback(() => {
    setWantVoice(false)
    audio.stop()
    chat.stopVoice()
    setVoiceState('off')
  }, [audio, chat])

  const toggleVoice = useCallback(() => {
    if (voiceState === 'off' || voiceState === 'error') {
      setWantVoice(true)
      return
    }
    stopVoice()
  }, [voiceState, stopVoice])

  /**
   * Tell the rest of the app the orb is up.
   *
   * The mobile menu pill floats in the same corner, so it steps aside while
   * somebody is talking (see `useSupportVoiceOpen` in nav-v4/mobile-nav).
   * A body attribute rather than shared state: the dock mounts in the
   * organisation layout and the nav renders from its own tree, with no
   * provider between them.
   *
   * Cleared on unmount as well as on close, or leaving the page mid-sentence
   * would hide the menu for good.
   */
  useEffect(() => {
    if (!open || !allowed) {
      delete document.body.dataset.supportVoice
      return
    }
    document.body.dataset.supportVoice = 'on'
    return () => {
      delete document.body.dataset.supportVoice
    }
  }, [open, allowed])

  const dismiss = useCallback(() => {
    setWantVoice(false)
    audio.stop()
    chat.stopVoice()
    setVoiceState('off')
    setOpen(false)
    setExpanded(false)
    setDraft('')
  }, [audio, chat])

  const submit = useCallback(() => {
    const text = draft.trim()
    if (!text) return
    chat.send(text)
    setDraft('')
  }, [draft, chat])

  // Both the hotkey and the open event are declared before `toggleVoice` and
  // `dismiss` exist, so they reach them through refs rather than re-binding
  // every render.
  const startVoiceRef = useRef<() => void>(() => undefined)
  const dismissRef = useRef<() => void>(() => undefined)
  const openRef = useRef(open)
  openRef.current = open

  // `openSupportDock()` from anywhere: the sidebar entry, a hotkey, a help
  // link inside a page. An event rather than a context because the callers
  // are spread across the old and new navigation trees, and neither is a
  // child of a provider we could put here without rewriting both.
  useEffect(() => {
    const onOpen = (event: Event) => {
      setOpen(true)
      // Opened BY VOICE when the caller asked for it: holding the mobile
      // menu pill is the same decision as pressing the chord, and landing
      // on a silent orb that has to be tapped again would undo the gesture.
      const detail = (event as CustomEvent<OpenSupportDockOptions>).detail
      if (detail?.voice) startVoiceRef.current()
    }
    window.addEventListener(SUPPORT_DOCK_OPEN, onOpen)
    return () => {
      window.removeEventListener(SUPPORT_DOCK_OPEN, onOpen)
    }
  }, [])

  /**
   * Cmd+/ opens it from anywhere.
   *
   * NOT cmd+K, which already opens the organisation search dialogue on every
   * page in here, focuses the search field on the org picker, and is claimed
   * locally by four more pages. Slash is the convention this codebase already
   * chose for an assistant (`components/agent-chat-dock/use-chat-hotkey.ts`)
   * for exactly that reason.
   *
   * BUBBLE PHASE, deliberately. The email campaign constructor binds the same
   * chord in capture phase and stops propagation, so its own assistant keeps
   * the shortcut on the one page that has one, and support takes it
   * everywhere else. Two assistants cannot both own a key; this is how they
   * share it without either having to know about the other.
   */
  useEffect(() => {
    // Not permitted here: leave the chord alone entirely rather than
    // swallowing it for somebody who has no dock to open.
    if (!permitted) return
    const onKey = (event: KeyboardEvent) => {
      if (!event.metaKey && !event.ctrlKey) return
      if (event.key !== '/') return
      event.preventDefault()
      /**
       * VOICE FIRST. The shortcut is for the moment somebody is stuck and
       * wants to ask, and asking out loud is faster than typing it. Opening
       * a text box and making them reach for the microphone afterwards puts
       * a step in front of the thing the chord is for.
       *
       * Nothing is taken away by starting with the mic: the composer is the
       * same box they are already looking at.
       */
      if (openRef.current) {
        dismissRef.current()
        return
      }
      setOpen(true)
      startVoiceRef.current()
    }
    /**
     * Escape leaves.
     *
     * Needed because the orb is now the MICROPHONE: pressing it mutes,
     * which is the obvious meaning of pressing a microphone, and that left
     * no visible way out other than the chord that opened it. Escape is
     * what every other overlay in the product answers to.
     *
     * Not while they are mid-sentence in the composer: Escape there is
     * somebody clearing a thought, not closing an assistant.
     */
    const onEscape = (event: KeyboardEvent) => {
      if (event.key !== 'Escape' || !openRef.current) return
      const active = document.activeElement
      if (active instanceof HTMLTextAreaElement && active.value !== '') return
      dismissRef.current()
    }
    window.addEventListener('keydown', onKey)
    window.addEventListener('keydown', onEscape)
    return () => {
      window.removeEventListener('keydown', onKey)
      window.removeEventListener('keydown', onEscape)
    }
  }, [permitted])

  const voiceLive = voiceState === 'listening' || voiceState === 'connecting'
  /**
   * Whichever side is making noise, so the dot reacts to both halves of the
   * conversation rather than looking dead while the person is talking.
   *
   * ABOVE THE EARLY RETURN, like every other hook here: `useAudioLevel`
   * cannot sit below it or the hook order changes the moment the dock
   * closes, which React treats as a different component.
   */
  const level = useAudioLevel(
    audio.speaking ? audio.outputAnalyser : audio.inputAnalyser,
    voiceLive
  )

  /**
   * Files on their way up, uploaded on selection rather than on send.
   *
   * Keyed to the case's thread so a file belongs to the conversation, not
   * to the socket: typed and spoken read the same thread, so a screenshot
   * pasted while typing is readable by the spoken agent on the next
   * utterance.
   */
  const files = useAttachments(orgId, chat.threadId ?? chat.caseId)

  /**
   * The whole conversation, for the full panel.
   *
   * Mapped rather than passed through, because the transcript on this
   * layer is lines of text and the chat block's shape carries avatars,
   * tool cards and a composer it does not need.
   */
  const turns = useMemo<TranscriptTurn[]>(
    () =>
      chat.messages
        .map((message) => {
          const parts = message.parts as {
            type: string
            text?: string
            attachment?: { id: string; filename: string; mimeType: string; url: string }
          }[]
          return {
            id: message.id,
            mine: message.role === 'user',
            // A named teammate replying alongside the agent. Without this
            // a staff member's words read as the assistant's, which in a
            // support conversation is the one confusion worth avoiding.
            author: message.authorName,
            text: parts
              .map((part) => (part.type === 'text' ? (part.text ?? '') : ''))
              .join('')
              .trim(),
            files: parts
              .filter((part) => part.type === 'attachment' && part.attachment)
              .map((part) => ({
                name: part.attachment?.filename ?? 'file',
                url: part.attachment?.url,
                kind: part.attachment?.mimeType.startsWith('image/')
                  ? ('image' as const)
                  : ('file' as const),
              })),
          }
        })
        // A turn with only files and no words is a complete request:
        // dropping a floor plan in and saying nothing.
        .filter((turn) => turn.text !== '' || turn.files.length > 0),
    [chat.messages],
  )

  /**
   * ABOVE THE EARLY RETURN, like every other hook in here. It landed
   * below it once and React threw "Rendered more hooks than during the
   * previous render" the moment the dock closed, which takes the whole
   * page down and not just the dock.
   */
  /**
   * The last thing it said, under the orb.
   *
   * `say` from the designs, and it renders from the transcript already on
   * the socket rather than from a tool call: spending a turn to caption
   * your own sentence would be a turn spent saying nothing.
   *
   * It is there for a noisy venue and for anybody who missed a name, and
   * it is the one part of the surface that earns its place in EVERY
   * conversation rather than only an ambiguous one.
   */
  const caption = useMemo(() => {
    for (let i = chat.messages.length - 1; i >= 0; i--) {
      const message = chat.messages[i]
      if (message.role !== 'assistant') continue
      const text = message.parts
        .map((part: { type: string; text?: string }) =>
          part.type === 'text' ? (part.text ?? '') : '',
        )
        .join('')
        .trim()
      return text === '' ? undefined : text
    }
    return undefined
  }, [chat.messages])

  startVoiceRef.current = () => {
    setWantVoice(true)
  }
  dismissRef.current = dismiss

  if (!allowed || !open) return null

  /**
   * THE ORB IS THE WHOLE SURFACE, for now.
   *
   * No pill, no composer, no transcript. Pressing the chord is somebody
   * deciding to ASK, out loud, and anything else on screen at that moment is
   * a decision they did not want to make. The typed surface still exists at
   * /support/cases; this is deliberately not it.
   *
   * It sits above everything and floats, so whatever page they were reading
   * stays readable behind it. Clicking it stops.
   */
  const orbState: OrbState = audio.speaking
    ? 'speaking'
    : chat.status === 'thinking' || chat.status === 'streaming'
      ? 'thinking'
      : voiceLive
        ? 'listening'
        : 'idle'

  return (
    <AgentSurface
      orbState={orbState}
      level={level}
      caption={caption}
      part={parts.current}
      turns={turns}
      /**
       * Thinking covers BOTH halves. `chat.status` is the typed run; the
       * spoken path sets it too, because a spoken turn that is running a
       * tool is just as silent and the orb alone does not say which of
       * "listening" and "working" it is doing.
       */
      thinking={chat.status === 'thinking' || chat.status === 'streaming'}
      expanded={expanded}
      onToggleExpanded={() => {
        setExpanded((was) => !was)
      }}
      diagram={diagram}
      onCloseDiagram={() => {
        setDiagram(null)
        setSavingDiagram(null)
      }}
      onSaveDiagram={(filename, contents) => {
        void saveDiagram(filename, contents)
      }}
      savingDiagram={savingDiagram}
      onNewDiagram={() => {
        setSavingDiagram(null)
        // A blank canvas. Excalidraw's own tools do the rest, and the
        // agent can draw into the same conversation afterwards.
        setDiagram({ title: 'Diagram', elements: [] })
      }}
      panels={panels.open}
      onTogglePanel={panels.toggle}
      renderPanel={(id: PanelId) => {
        if (id === 'context') {
          return (
            <ContextPanel
              onClose={() => {
                panels.close('context')
              }}
              route={location.pathname}
              venueSerial={turnContext.venueSerial}
              venueName={
                turnContext.venueSerial
                  ? findVenueAlias(turnContext.venueSerial)
                  : undefined
              }
              orgId={orgId}
              orgName={organisation?.name}
              caseId={chat.caseId}
              movedTo={movedTo}
              voice={voiceState}
              muted={audio.muted}
            />
          )
        }
        if (id === 'workspace') {
          return (
            <WorkspacePanel
              onClose={() => {
                panels.close('workspace')
              }}
              orgId={orgId}
              onOpenDiagram={(title, elements) => {
                setSavingDiagram(null)
                setDiagram({ title, elements })
              }}
            />
          )
        }
        return (
          <PagesPanel
            onClose={() => {
              panels.close('pages')
            }}
            pathname={location.pathname}
            onGo={(path, label) => {
              onNavigate({ path, label, pageId: `panel-${path}` })
            }}
          />
        )
      }}
      pending={files.pending.map((file) => ({
        name: file.error ? `${file.name} · ${file.error}` : file.name,
        kind: file.kind,
      }))}
      onFiles={files.add}
      onDropPending={(name) => {
        // The chip shows the error appended, so match on the real name.
        files.remove(name.split(' · ')[0])
      }}
      muted={audio.muted}
      onToggleMute={() => {
        /**
         * Muting tells TRUNK to stop as well, not just the speaker.
         *
         * Dropping the audio locally would leave the model writing an
         * answer nobody is going to hear, and the server would keep
         * sending it. `interruptVoice` is the same signal a barge-in
         * sends, which is the right one: somebody has asked it to stop
         * talking.
         */
        if (!audio.muted) chat.interruptVoice()
        audio.toggleMute()
      }}
      onSend={(text) => {
        /**
         * Only files that actually landed go up. One still uploading is
         * dropped from the turn rather than sent as a broken URL the
         * agent would try to read and fail on.
         */
        /**
         * Only what actually landed goes up, as ASSET IDS rather than
         * urls: the agent reads the bytes server-side with the task IAM
         * role. One still uploading is left out of the turn rather than
         * sent as an id the agent would fail to read.
         */
        chat.send(text, files.ready())
        files.clear()
      }}
      onChoose={(option) => {
        /**
         * A tap and a spoken answer converge HERE, before the agent sees
         * either. Pressing "The Bothy Perth" is the same event as saying
         * it, so there is one code path and no way for the surface to
         * quietly become a form with a different contract.
         *
         * THE MOVE IS OPTIMISTIC. When the option carries a route, go
         * there now: trunk resolved it when it built the choice, so
         * waiting on a model to re-derive a destination it offered a
         * second ago is seconds spent learning nothing. The agent still
         * gets the message and still answers; its own navigate-to then
         * lands on the page we are already on, which `onNavigate` treats
         * as a no-op.
         */
        parts.clear()
        if (option.goTo) onNavigate(option.goTo)
        chat.send(option.label)
      }}
    />
  )
}

/** Window event that opens the dock from anywhere in the app. */
export const SUPPORT_DOCK_OPEN = 'stampede:support-dock:open'

export interface OpenSupportDockOptions {
  /** Start listening straight away, as the keyboard chord does. */
  voice?: boolean
}

/**
 * Open the dock. Safe to call from any surface, old nav or new.
 *
 * The caller says whether it is a spoken opening because the two entry
 * points differ: a help link wants the surface, holding the menu pill wants
 * the microphone. The dock cannot tell them apart on its own.
 */
export function openSupportDock(options: OpenSupportDockOptions = {}): void {
  window.dispatchEvent(
    new CustomEvent<OpenSupportDockOptions>(SUPPORT_DOCK_OPEN, {
      detail: { voice: options.voice ?? false },
    })
  )
}
